import { SWING_EVENT_MAP, type EventDescriptor } from './event-map'

interface ResolveRegisteredEventsParams {
  elementType?: string
  elementMetadata?: any
  eventActions?: Record<string, any>
}

const EXECUTABLE_ACTION_FIELDS = [
  'onClick',
  'onChange',
  'onSelectionChanged',
  'onSelectionChange',
  'onInlineMenuShow',
  'inlineDoFirstAction',
  'onSuggest',
  'onImport',
  'event.fill',
  'event.fileChanged',
  'event.onChange',
  'event.chainCalled',
]

const nonEmptyText = (value: any): boolean => {
  return typeof value === 'string' && value.trim().length > 0
}

const hasExecutableTag = (metadata: any, tag: string): boolean => {
  if (!metadata || typeof metadata !== 'object' || !tag) return false
  return nonEmptyText(metadata?.[tag]?._text)
}

const hasExecutableActionCode = (action: any): boolean => {
  if (!action || typeof action !== 'object') return false

  return EXECUTABLE_ACTION_FIELDS.some((key) => nonEmptyText(action?.[key]?._text))
}

const getDescriptorsForType = (elementType?: string): EventDescriptor[] => {
  const normalizedType = (elementType || '').toUpperCase()
  return SWING_EVENT_MAP[normalizedType] || []
}

const getAllEventNames = (descriptor: EventDescriptor): string[] => {
  return [descriptor.eventName, ...(descriptor.aliases || [])].filter(Boolean)
}

export const getSupportedEventNamesForType = (elementType?: string): Set<string> => {
  const names = new Set<string>()
  getDescriptorsForType(elementType).forEach((descriptor) => {
    getAllEventNames(descriptor).forEach((name) => names.add(name))
  })
  return names
}

export const resolveRegisteredEvents = ({
  elementType,
  elementMetadata,
  eventActions,
}: ResolveRegisteredEventsParams): Set<string> => {
  const registered = new Set<string>()
  const descriptors = getDescriptorsForType(elementType)

  descriptors.forEach((descriptor) => {
    const hasElementCode = descriptor.tags.some((tag) => hasExecutableTag(elementMetadata, tag))
    const hasEventActionCode = getAllEventNames(descriptor).some((eventName) =>
      hasExecutableActionCode(eventActions?.[eventName])
    )

    if (hasElementCode || hasEventActionCode) {
      getAllEventNames(descriptor).forEach((name) => registered.add(name))
    }
  })

  return registered
}
